import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Play, ArrowRight, Mail, Leaf, ExternalLink, Menu, X } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo component
const Logo = ({ className = "" }) => (
  <img 
    src="/axios-logo.svg" 
    alt="Axios" 
    className={`h-12 md:h-14 w-auto [filter:brightness(0)_invert(1)] ${className}`}
    data-testid="axios-logo"
  />
);

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "glass-dark py-3" : "py-6"
      }`}
      data-testid="main-navigation"
    >
      <div className="section-container flex items-center justify-between">
        <Logo />
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollToSection("vision")} className="nav-link" data-testid="nav-vision">Vision</button>
          <button onClick={() => scrollToSection("team")} className="nav-link" data-testid="nav-team">Team</button>
          <button onClick={() => scrollToSection("greece")} className="nav-link" data-testid="nav-greece">Greece</button>
          <button 
            onClick={() => scrollToSection("contact")} 
            className="btn-primary !py-2 !px-5 text-sm"
            data-testid="nav-contact-btn"
          >
            Contact
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden glass-dark mt-4 rounded-2xl mx-4 p-6"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-4">
            <button onClick={() => scrollToSection("vision")} className="nav-link text-left py-2">Vision</button>
            <button onClick={() => scrollToSection("team")} className="nav-link text-left py-2">Team</button>
            <button onClick={() => scrollToSection("greece")} className="nav-link text-left py-2">Greece</button>
            <button onClick={() => scrollToSection("contact")} className="btn-primary mt-2">Contact</button>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-background">
        <img
          src="https://images.unsplash.com/photo-1624040440822-06fd7b7fdc41?q=80&w=2070&auto=format&fit=crop"
          alt="Cannabis leaves"
          loading="eager"
        />
      </div>
      <div className="hero-overlay" />
      
      <div className="hero-content">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight leading-tight mb-6"
          data-testid="hero-headline"
        >
          Cultivating the Future<br />
          <span className="text-[#C4CCBB]">of Medicine</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Leading the European medical cannabis revolution from the heart of Greece. 
          Building one of the largest, most advanced facilities in the EU.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button 
            onClick={scrollToContact} 
            className="btn-primary flex items-center justify-center gap-2"
            data-testid="hero-cta-contact"
          >
            Get in Touch <ArrowRight size={18} />
          </button>
          <a
            href="https://vimeo.com/1121948036"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary flex items-center justify-center gap-2"
            data-testid="hero-cta-video"
          >
            <Play size={18} /> Watch Our Vision
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 bg-white/60 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Vision Section
const VisionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="vision" className="py-24 md:py-32 bg-[#F5F3EF]" data-testid="vision-section">
      <div className="section-container">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#8B9A7D] font-semibold text-sm tracking-wider uppercase mb-4 block">
              Our Vision
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#2D2D2D] mb-6 leading-tight" data-testid="vision-heading">
              Redefining
              <br />
              <span className="gradient-text">Medical Wellness</span>
            </h2>
            <p className="text-lg text-[#6B6B6B] leading-relaxed mb-6">
              We are building one of the largest, most advanced medical cannabis facilities in the European Union. 
              Combining Greek agricultural heritage with state-of-the-art pharmaceutical technology to deliver 
              premium care that improves lives.
            </p>
            <p className="text-lg text-[#6B6B6B] leading-relaxed">
              Our mission goes beyond cultivation — we're creating a new standard for medical cannabis excellence, 
              ensuring quality, compliance, and compassion at every step.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="vision-image h-[400px] md:h-[500px]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_cannabis-wellness-1/artifacts/sc9s50ej_IMG_0473.jpeg"
              alt="Cannabis trichomes macro"
              className="w-full h-full"
              loading="lazy"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const team = [
    {
      name: "Boris Blatnik",
      role: "Head of EU Operations",
      initials: "BB",
    },
    {
      name: "Connor McCaffrey",
      role: "Chief Executive Officer",
      initials: "CM",
    },
    {
      name: "Matthew Harrison",
      role: "Chief Operating Officer",
      initials: "MH",
    },
  ];

  return (
    <section id="team" className="py-24 md:py-32 bg-[#F5F3EF]" data-testid="team-section">
      <div className="section-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#8B9A7D] font-semibold text-sm tracking-wider uppercase mb-4 block">
            Leadership
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#2D2D2D] mb-4" data-testid="team-heading">
            An Elite Team
          </h2>
          <p className="text-lg text-[#6B6B6B] max-w-2xl mx-auto">
            Industry veterans with proven track records in cannabis operations, finance, and international expansion.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8" data-testid="team-grid">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="bg-white rounded-2xl p-8 text-center shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_-12px_rgba(139,154,125,0.2)] transition-all duration-400 hover:-translate-y-2"
              data-testid={`team-member-${member.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#8B9A7D] to-[#4A5240] flex items-center justify-center">
                <span className="text-2xl font-bold text-white">{member.initials}</span>
              </div>
              <h3 className="text-xl font-bold text-[#2D2D2D] mb-2">{member.name}</h3>
              <p className="text-[#8B9A7D] font-medium">{member.role}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Greece Advantage Section
const GreeceSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  const advantages = [
    { title: "Optimal Climate", description: "300+ days of sunshine, Mediterranean conditions" },
    { title: "EU Gateway", description: "Strategic access to European markets" },
    { title: "Progressive Legislation", description: "Favorable regulatory framework" },
    { title: "Cost Efficiency", description: "Competitive operational costs" },
  ];

  return (
    <section id="greece" className="parallax-section min-h-[600px]" ref={ref} data-testid="greece-section">
      <div className="parallax-bg">
        <motion.img
          style={{ y }}
          src="https://customer-assets.emergentagent.com/job_cannabis-wellness-1/artifacts/uni7loci_IMG_0475.jpeg"
          alt="The Parthenon, Athens, Greece"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D2D2D]/80 via-[#2D2D2D]/60 to-transparent" />
      </div>

      <div className="section-container relative z-10 py-24">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-2xl"
        >
          <span className="text-[#C4CCBB] font-semibold text-sm tracking-wider uppercase mb-4 block">
            Strategic Location
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-testid="greece-heading">
            The Greek
            <br />
            <span className="text-[#C4CCBB]">Advantage</span>
          </h2>
          <p className="text-lg text-white/80 leading-relaxed mb-8">
            Greece offers the perfect combination of natural resources, regulatory support, 
            and strategic positioning for medical cannabis excellence.
          </p>

          <div className="grid grid-cols-2 gap-6">
            {advantages.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="glass-dark p-4 rounded-xl"
                data-testid={`advantage-${index}`}
              >
                <Leaf className="w-5 h-5 text-[#C4CCBB] mb-2" />
                <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                <p className="text-white/60 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Partners Section
const PartnersSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const partners = [
    "Papapolitis & Papapolitis",
    "William Fry",
    "KPC Finance",
    "BS&MC",
    "Greentech",
    "Noisis",
    "Oppenheimer Group",
  ];

  return (
    <section className="py-20 md:py-24 bg-[#F5F3EF]" data-testid="partners-section">
      <div className="section-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#8B9A7D] font-semibold text-sm tracking-wider uppercase mb-4 block">
            Task Force
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#2D2D2D]" data-testid="partners-heading">
            Strategic Partners
          </h2>
        </motion.div>

        <div className="marquee-container">
          <div className="marquee-track animate-scroll">
            {[...partners, ...partners].map((partner, index) => (
              <div
                key={`${partner}-${index}`}
                className="flex-shrink-0 px-8 py-4 glass rounded-xl"
                data-testid={`partner-${index}`}
              >
                <span className="text-[#2D2D2D] font-semibold whitespace-nowrap">{partner}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Thank you for your interest! We'll be in touch soon.");
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
      <div className="section-container">
        <div className="max-w-3xl mx-auto">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-[#8B9A7D] font-semibold text-sm tracking-wider uppercase mb-4 block">
              Get in Touch
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#2D2D2D] mb-4" data-testid="contact-heading">
              Let's Connect
            </h2>
            <p className="text-lg text-[#6B6B6B]">
              Interested in learning more? We'd love to hear from you.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Input
                  type="text"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="contact-input"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="contact-input"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div>
              <Input
                type="text"
                placeholder="Company / Organization"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="contact-input"
                data-testid="contact-company-input"
              />
            </div>
            <div>
              <Textarea
                placeholder="Tell us about your interest..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="contact-input resize-none"
                data-testid="contact-message-input"
              />
            </div>
            <div className="text-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary !bg-[#8B9A7D] hover:!bg-[#4A5240] text-white px-12"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 bg-[#2D2D2D]" data-testid="footer">
      <div className="section-container">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img 
              src="/axios-logo.svg" 
              alt="Axios" 
              className="h-10 w-auto [filter:brightness(0)_invert(1)] opacity-80"
            />
            <span className="text-white/60 text-sm">
              © {new Date().getFullYear()} Axios. All rights reserved.
            </span>
          </div>
          
          <div className="flex items-center gap-6">
            <a 
              href="https://vimeo.com/1121948036" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/60 hover:text-[#C4CCBB] transition-colors text-sm flex items-center gap-1"
              data-testid="footer-video-link"
            >
              Watch Video <ExternalLink className="w-3 h-3" />
            </a>
            <a 
              href="mailto:info@axios.life" 
              className="text-white/60 hover:text-[#C4CCBB] transition-colors text-sm flex items-center gap-1"
              data-testid="footer-email-link"
            >
              <Mail className="w-4 h-4" /> Contact
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Navigation />
      <HeroSection />
      <VisionSection />
      <TeamSection />
      <GreeceSection />
      <PartnersSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
